/**
 * Pilot Analytics client — additive layer.
 *
 * Canonical spec: /app/memory/MALDEK_PILOT_ANALYTICS_PATCH.md
 *
 * Usage from any surface:
 *   import { track } from "../lib/analytics";
 *   track({
 *     event_type: "module_view",
 *     surface: "walkthrough",
 *     body_system: "cardio",
 *     topic: "Heart failure",
 *   });
 *
 * track() is fire-and-forget; if the user has not consented the backend
 * silently rejects with `accepted: false` and nothing is stored.
 *
 * raw_user_id and cohort_id are read from localStorage so a single
 * call site stays clean.  raw_user_id is hashed server-side — the
 * frontend never holds an identifier we wouldn't be comfortable
 * shipping to the analytics surface.
 */
import { api } from "./api";

const KEYS = {
  uid:     "maldek.pilot.uid",
  cohort:  "maldek.pilot.cohort",
  consent: "maldek.pilot.consent",  // "yes" | "no" | null
};

function read(k) {
  try { return localStorage.getItem(k); } catch { return null; }
}
function write(k, v) {
  try { localStorage.setItem(k, v); } catch { /* ignore */ }
}

function uuid() {
  // Tiny, browser-safe random id for first-touch users.  Hashed
  // server-side so the analytics layer never sees this raw value.
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "u-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function getRawUserId() {
  let id = read(KEYS.uid);
  if (!id) { id = uuid(); write(KEYS.uid, id); }
  return id;
}

export function getCohortId() {
  return read(KEYS.cohort) || null;
}

export function setCohortId(id) {
  if (id) write(KEYS.cohort, id);
}

export function getConsent() {
  const v = read(KEYS.consent);
  if (v === "yes") return true;
  if (v === "no")  return false;
  return null;
}

export async function setConsent(consent) {
  write(KEYS.consent, consent ? "yes" : "no");
  const raw_user_id = getRawUserId();
  const cohort_id   = getCohortId();
  try {
    await api.post("/analytics/consent", { raw_user_id, cohort_id, consent });
  } catch { /* offline first — consent flag is local */ }
}

export async function track(event) {
  if (getConsent() !== true) return; // hard gate
  const body = {
    raw_user_id: getRawUserId(),
    cohort_id:   getCohortId(),
    ...event,
  };
  try { await api.post("/analytics/event", body); } catch { /* swallow */ }
}

/* Convenience: start a timer and return a fn that emits a
 * `time_on_section` event on stop.  Use on mount / unmount. */
export function startSectionTimer(surface, topic, extra = {}) {
  const t0 = Date.now();
  return () => {
    const ms = Date.now() - t0;
    if (ms < 800) return; // ignore micro-views
    track({
      event_type: "time_on_section",
      surface,
      topic,
      decision_ms: ms,
      ...extra,
    });
  };
}
